import type { OutlineCollectionRef, OutlineRegion, PageOutline } from '@/lib/api/types';

/**
 * Where records appear on the website, from the page outlines (GET /admin/page-outlines):
 * every page section that lists a collection names it, with the website's own filter
 * (`family=lv`, `placement=home_plates`, `group=board`). A record's editor previews the best
 * of those places; a list screen says where its records are shown.
 */
export interface Place {
  outline: PageOutline;
  region: OutlineRegion;
}

/**
 * The page a collection belongs to when several list it: the Projects page for projects
 * (Home also shows a few), Contact for offices, Careers for vacancies. A record's own page
 * (the article screen) beats them all.
 */
const HOME_PAGE: Record<string, string> = {
  industries: 'industries',
  projects: 'projects',
  articles: 'insights',
  'job-postings': 'careers',
  locations: 'contact',
  subsidiaries: 'about',
  products: 'products',
  'product-families': 'products',
  'team-members': 'investor-relations',
};

/** Filter key on a section → the record field the website narrows by. */
const FILTER_FIELD: Record<string, string> = { placement: 'placements' };

/** Whether a record (its `doc`) passes a section's filter, as the website applies it. */
export function passesFilter(
  ref: OutlineCollectionRef,
  doc: Record<string, unknown> | null | undefined,
): boolean {
  for (const [key, want] of Object.entries(ref.filter ?? {})) {
    const value = doc?.[FILTER_FIELD[key] ?? key];
    if (Array.isArray(value) ? !value.includes(want) : value !== want) return false;
  }
  return true;
}

/**
 * Every section that would show this record, best first. A section the preview cannot
 * render (a page the database has no row for yet) is skipped. Pass no `doc` to ask where
 * the collection appears at all, whatever its records hold.
 */
export function recordPlaces(
  outlines: readonly PageOutline[],
  collection: string,
  doc?: Record<string, unknown> | null,
): Place[] {
  const scored: { place: Place; score: number; order: number }[] = [];
  let order = 0;
  for (const outline of outlines) {
    if (!outline.previewable || (outline.page && !outline.record)) continue;
    for (const region of outline.regions) {
      order += 1;
      if (!region.on_page) continue;
      const ref = region.collections.find((c) => c.collection === collection);
      if (!ref) continue;
      const filtered = Object.keys(ref.filter ?? {}).length > 0;
      if (doc !== undefined && !passesFilter(ref, doc)) continue;
      const score =
        (region.component === 'article' ? 100 : 0) +
        (filtered && doc !== undefined ? 10 : 0) +
        (HOME_PAGE[collection] === outline.key ? 5 : 0) +
        (outline.page ? 1 : 0);
      scored.push({ place: { outline, region }, score, order });
    }
  }
  return scored.sort((a, b) => b.score - a.score || a.order - b.order).map((s) => s.place);
}

/** The pages (each once, best first) where a collection or one record of it is shown. */
export function placePages(places: readonly Place[]): Place[] {
  const seen = new Set<string>();
  return places.filter((p) => {
    if (seen.has(p.outline.key)) return false;
    seen.add(p.outline.key);
    return true;
  });
}

/**
 * Lists the website never shows: their records cannot be previewed, and editing them
 * changes nothing a visitor sees. Settings → Brand assets replaced the site-asset slots, and
 * only the board of the team-member register is rendered (Investor relations).
 */
export function notOnWebsite(
  outlines: readonly PageOutline[],
  collection: string,
  doc?: Record<string, unknown> | null,
): boolean {
  return recordPlaces(outlines, collection, doc).length === 0;
}
